import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate, useLocation } from 'react-router-dom';

// Import AI provider logos
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import awsLogo from '../assets/cloud-providers/aws.svg';
import { toastError, toastSuccess } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { buildTimeSeriesParams, formatV2Series } from '../utils/v2StatsHelpers';
import { AiLlmTypes } from '@flashbacktech/flashbackclient/api';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';


// Format numbers with suffixes
const formatNumber = (num: number): string => {
  if (num >= 1000000000000) return (num / 1000000000000).toFixed(2) + 'T';
  if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'G';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
  return num.toString();
};

// Round max value to nice domain for Y-axis (same as StorageUnitDetails)
const roundToNiceDomain = (maxValue: number): number => {
  if (maxValue === 0) return 5;
  if (maxValue < 0.001) return 0.005;
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
  const possibleSteps = [
    magnitude * 0.5, magnitude * 1, magnitude * 1.5, magnitude * 2,
    magnitude * 2.5, magnitude * 5, magnitude * 10,
  ];
  for (const step of possibleSteps) {
    if (step >= maxValue) return step;
  }
  return magnitude * 10;
};

// Generate 10 evenly distributed tick indices for X-axis
const getXAxisTickIndices = (dataLength: number): number[] => {
  if (dataLength <= 10) return Array.from({ length: dataLength }, (_, i) => i);
  const indices: number[] = [];
  const step = (dataLength - 1) / 9;
  for (let i = 0; i < 10; i++) indices.push(Math.round(i * step));
  return indices;
};

// Format tokens
const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) return (tokens / 1000000).toFixed(2) + 'M';
  if (tokens >= 1000) return (tokens / 1000).toFixed(2) + 'K';
  return tokens.toFixed(0);
};

// Get provider icon (using correct logos from cloud-providers)
const getProviderIcon = (aiType: string): JSX.Element => {
  switch (aiType?.toUpperCase()) {
    case 'OPENAI':
      return (
        <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px' }} fill="#10A37F">
          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
        </svg>
      );
    case 'ANTHROPIC':
      return <img src={anthropicLogo} alt="Anthropic" style={{ width: '18px', height: '18px' }} />;
    case 'GEMINI':
    case 'GOOGLE':
      return <img src={googleCloudLogo} alt="Google" style={{ width: '18px', height: '18px' }} />;
    case 'BEDROCK':
    case 'AWS':
      return <img src={awsLogo} alt="AWS" style={{ width: '18px', height: '18px' }} />;
    default:
      return (
        <span style={{ fontSize: '10px', fontWeight: 700, color: '#8B5CF6' }}>
          AI
        </span>
      );
  }
};

// Get AI type display name
const getAITypeName = (aiType: string): string => {
  const type = aiType?.toLowerCase() || '';
  if (type.includes('openai') || type.includes('gpt')) return 'OpenAI';
  if (type.includes('anthropic') || type.includes('claude')) return 'Anthropic';
  if (type.includes('google') || type.includes('gemini')) return 'Google AI';
  if (type.includes('mistral')) return 'Mistral AI';
  if (type.includes('cohere')) return 'Cohere';
  return aiType || 'Unknown';
};

interface V2SeriesPoint {
  ts: number;
  tokensIn: number;
  tokensOut: number;
  requests: number;
  latencyMs: number;
  policyTokensIn: number;
  policyTokensOut: number;
}

interface AILLMDetailsData {
  id: string;
  name: string;
  aiType: string;
  endpoint: string;
  status: string;
  createdAt: string;
  repos: Array<{ id: string; name: string; createdAt: string }>;
}

function AILLMDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  
  // Extract llmId from pathname: /ai/llm/:id
  const pathParts = location.pathname.split('/');
  const llmId = pathParts[pathParts.length - 1];

  const [loading, setLoading] = useState(true);
  const [llmConfig, setLlmConfig] = useState<AILLMDetailsData | null>(null);
  const [statusHovered, setStatusHovered] = useState(false);
  const [bridgeNodes, setBridgeNodes] = useState<Array<{nodeId: string; status: string; latency: number; host: string}>>([]);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [llmStats, setLlmStats] = useState<{ totalApiCalls: number; totalTokensIn: number; totalTokensOut: number } | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // State for token visibility toggle in chart
  const [visibleTokens, setVisibleTokens] = useState({ tokensIn: true, tokensOut: true });
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [v2Series, setV2Series] = useState<V2SeriesPoint[]>([]);
  const [dailyStatsLoading, setDailyStatsLoading] = useState(false);

  const chartData = React.useMemo(() => {
    if (v2Series.length === 0) {
      const result: Array<{
        date: string; fullDate: string;
        tokensIn: number; tokensOut: number; apiCalls: number;
        policyViolations: number; alerts: number; blocks: number; latency: number;
      }> = [];
      const now = new Date();
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        result.push({
          date: `${month}/${day}`,
          fullDate: `${month}/${day}/${d.getFullYear()}`,
          tokensIn: 0, tokensOut: 0, apiCalls: 0,
          policyViolations: 0, alerts: 0, blocks: 0, latency: 0,
        });
      }
      return result;
    }

    const formatted = formatV2Series(v2Series, '30d');
    return formatted.map(p => {
      const d = new Date(p.ts * 1000);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return {
        date: p.name,
        fullDate: `${month}/${day}/${d.getFullYear()}`,
        tokensIn: p.tokensIn || 0,
        tokensOut: p.tokensOut || 0,
        apiCalls: p.requests || 0,
        policyViolations: 0,
        alerts: 0,
        blocks: 0,
        latency: p.latencyMs || 0,
      };
    });
  }, [v2Series]);

  // Aliases for backward compatibility
  const tokenActivityData = chartData;

  // Calculate totals from chart data
  const totalTokensInFromChart = React.useMemo(() => 
    chartData.reduce((sum, item) => sum + item.tokensIn, 0), [chartData]);
  const totalTokensOutFromChart = React.useMemo(() => 
    chartData.reduce((sum, item) => sum + item.tokensOut, 0), [chartData]);
  const totalApiCallsFromChart = React.useMemo(() => 
    chartData.reduce((sum, item) => sum + item.apiCalls, 0), [chartData]);
  const totalViolationsFromChart = React.useMemo(() => 
    chartData.reduce((sum, item) => sum + item.policyViolations, 0), [chartData]);

  useEffect(() => {
    const loadLLMDetails = async () => {
      if (!authState?.token || !llmId) return;
      
      setLoading(true);
      try {
        // Try to load from API
        const response = await withTokenRefresh(() => apiClient.getAiLlms()) as any;
        if (response?.success && response?.aiLlms) {
          const config = response.aiLlms.find((c: any) => c.id === llmId);
          if (config) {
            setLlmConfig({
              id: config.id,
              name: config.configName || config.name || 'Unnamed',
              aiType: config.aiType || 'OpenAI',
              endpoint: config.endpoint || '',
              status: 'Active',
              createdAt: config.createdAt || new Date().toISOString(),
              repos: config.repos || [],
            });
          }
        }
      } catch (error) {
        console.error('Error loading LLM details:', error);
        // Use fallback data if API fails
        setLlmConfig({
          id: llmId,
          name: 'LLM Configuration',
          aiType: 'OpenAI',
          endpoint: 'https://api.openai.com/v1',
          status: 'Active',
          createdAt: new Date().toISOString(),
          repos: [],
        });
      } finally {
        setLoading(false);
      }
    };

    // Load bridge nodes from API
    const loadBridgeNodes = async () => {
      setLoadingNodes(true);
      try {
        const response = await withTokenRefresh(() => apiClient.getNodeInfo(2)) as any;
        let nodesList: Array<{nodeId: string; status: string; latency: number; host: string}> = [];
        
        if (response?.success === true && Array.isArray(response?.data)) {
          nodesList = response.data.map((n: any) => ({
            nodeId: n.url || n.ip || 'unknown',
            status: n.status === 'ACTIVE' || n.status === 'ONLINE' ? 'ONLINE' : 'OFFLINE',
            latency: n.latency || Math.floor(Math.random() * 100) + 20,
            host: n.ip || n.url || '',
          }));
        } else if (Array.isArray(response)) {
          nodesList = response.map((n: any) => ({
            nodeId: n.url || n.ip || 'unknown',
            status: n.status === 'ACTIVE' || n.status === 'ONLINE' ? 'ONLINE' : 'OFFLINE',
            latency: n.latency || Math.floor(Math.random() * 100) + 20,
            host: n.ip || n.url || '',
          }));
        }
        setBridgeNodes(nodesList);
      } catch (error) {
        console.error('Error loading bridge nodes:', error);
        setBridgeNodes([]);
      } finally {
        setLoadingNodes(false);
      }
    };

    const loadLLMStats = async () => {
      setLoadingStats(true);
      try {
        const tsParams = buildTimeSeriesParams(null, null, '30d');
        const response = await withTokenRefresh(() =>
          apiClient.getAiGatewayStats(tsParams as any)
        ) as any;
        if (response?.success && response?.series?.length > 0) {
          const totals = response.series.reduce(
            (acc: any, p: any) => ({
              totalApiCalls: acc.totalApiCalls + (p.requests || 0),
              totalTokensIn: acc.totalTokensIn + (p.tokensIn || 0),
              totalTokensOut: acc.totalTokensOut + (p.tokensOut || 0),
            }),
            { totalApiCalls: 0, totalTokensIn: 0, totalTokensOut: 0 },
          );
          setLlmStats(totals);
        }
      } catch (error) {
        console.error('Error loading LLM stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadLLMDetails();
    loadBridgeNodes();
    loadLLMStats();
  }, [authState?.token, llmId, withTokenRefresh, apiClient]);

  useEffect(() => {
    const loadV2TimeSeries = async () => {
      if (!authState?.token || !llmId) return;
      try {
        setDailyStatsLoading(true);
        const tsParams = buildTimeSeriesParams(null, null, '30d');
        const response = await withTokenRefresh(() =>
          apiClient.getAiGatewayStats(tsParams as any)
        ) as any;
        if (response?.success && response?.series) {
          setV2Series(response.series);
        }
      } catch (error) {
        console.error('[AILLMDetails] Error loading V2 AI stats:', error);
      } finally {
        setDailyStatsLoading(false);
      }
    };
    loadV2TimeSeries();
  }, [authState?.token, llmId, withTokenRefresh, apiClient]);

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toastSuccess('Copied to clipboard');
      })
      .catch(() => {
        toastError('Failed to copy to clipboard');
      });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Typography>Loading LLM details...</Typography>
      </div>
    );
  }

  if (!llmConfig) {
    return (
      <div className="flex justify-center items-center h-full">
        <Typography>LLM configuration not found</Typography>
      </div>
    );
  }

  // Calculate connection status based on bridge nodes
  const connectedNodes = bridgeNodes.filter(n => n.status === 'ONLINE').length;
  const disconnectedNodes = bridgeNodes.filter(n => n.status !== 'ONLINE').length;
  const totalNodes = bridgeNodes.length;
  
  // Global status logic: Connected (≥1 online), Warning (0 online but has nodes), Disconnected (all offline)
  const globalStatus = connectedNodes >= 1 ? 'Connected' : (totalNodes > 0 ? 'Warning' : 'Disconnected');
  const statusColor = globalStatus === 'Connected' ? '#059669' : globalStatus === 'Warning' ? '#D97706' : '#DC2626';
  const statusBg = globalStatus === 'Connected' ? '#ECFDF5' : globalStatus === 'Warning' ? '#FFFBEB' : '#FEF2F2';
  const statusBorder = globalStatus === 'Connected' ? '#A7F3D0' : globalStatus === 'Warning' ? '#FDE68A' : '#FECACA';

  return (
    <div className="h-full overflow-y-auto" style={{ backgroundColor: '#FFFFFF', paddingLeft: '40px', paddingRight: '40px', paddingTop: '24px', paddingBottom: '24px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          AI LLM Details
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
            marginBottom: '16px',
          }}
        >
          View and manage your AI Language Model configuration and metrics
        </Typography>
        <Button
          variant="text"
          startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
          onClick={() => navigate('/ai/llm')}
          sx={{ color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
        >
          Back to LLM
        </Button>
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Config Details Card */}
          <div className="flex justify-center">
            <div className="w-[1080px] h-[85px] flex items-center justify-between px-4 border-b border-[#E9EAEB] mb-6">
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '200px',
                      backgroundColor: '#FFFFFF',
                      border: '0.5px solid #E5E7EB',
                    }}
                  >
                    {getProviderIcon(llmConfig.aiType)}
                  </div>
                  <Typography
                    sx={{ color: '#101828', fontWeight: 600, fontSize: '20px', lineHeight: '30px' }}
                  >
                    {llmConfig.name}
                  </Typography>
                </div>
                {/* Buttons row under title */}
                <div className="mt-4 flex items-center space-x-2">
                  {/* Short ID button */}
                  <button
                    title="Copy LLM ID"
                    onClick={() => handleCopy(llmConfig.id)}
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      cursor: 'pointer',
                    }}
                  >
                    {llmConfig.id.slice(0, 8)}...
                  </button>

                  {/* Provider badge */}
                  <button
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#617BFF',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {getAITypeName(llmConfig.aiType)}
                  </button>

                  {/* Status badge with hover for connection details */}
                  <Box
                    onMouseEnter={() => setStatusHovered(true)}
                    onMouseLeave={() => setStatusHovered(false)}
                    sx={{
                      height: 22,
                      background: statusHovered ? statusBg : '#FFFFFF',
                      borderRadius: '6px',
                      border: `1px solid ${statusHovered ? statusBorder : '#D5D7DA'}`,
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: statusHovered ? statusColor : '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: statusHovered ? statusColor : (llmConfig.status === 'Active' ? '#17B26A' : '#F04438'),
                        display: 'inline-block',
                        marginRight: '6px',
                        transition: 'background 0.2s ease',
                      }}
                    />
                    <span>{statusHovered ? globalStatus : llmConfig.status}</span>
                  </Box>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Reload button */}
                <button
                  onClick={() => window.location.reload()}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #D1D5DB',
                  }}
                  title="Reload data"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>

                {/* Settings gear icon - opens edit directly */}
                <button
                  onClick={() => {
                    // Navigate to AI LLM list with edit mode for this config
                    navigate('/ai/llm', { state: { editId: llmConfig.id, editProvider: llmConfig.aiType } });
                  }}
                  title="Settings"
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #D1D5DB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </button>

                {/* Delete button */}
                <button
                  onClick={() => setDeleteDialogOpen(true)}
                  title="Delete"
                  className="transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #FEE2E2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#EF4444',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#FEE2E2';
                    e.currentTarget.style.borderColor = '#EF4444';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#FEE2E2';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Graph Cards - 2x2 layout */}
          <div className="flex flex-col mt-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4 justify-items-center">
              {/* Requests Card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div className="absolute w-full h-full rounded-xl" style={{ backgroundColor: '#FDFDFD', borderRadius: '12px', border: '1px solid #E9EAEB' }}>
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Requests (30d)</Typography>
                    <Typography style={{ color: '#535862', fontWeight: 500, fontSize: '13px' }}>{dailyStatsLoading ? '...' : formatNumber(totalApiCallsFromChart)}</Typography>
                  </div>
                </div>
                <div className="absolute bg-white rounded-xl shadow-sm" style={{ width: '528px', height: '330px', top: '42px', borderRadius: '12px', border: '1px solid #E9EAEB', overflow: 'visible' }}>
                  <div className="p-4">
                    <div style={{ height: 290 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <defs>
                            <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#617BFF" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            interval={2}
                          />
                          <YAxis
                            domain={[0, (dataMax: number) => roundToNiceDomain(Math.max(dataMax, 25))]}
                            ticks={(() => {
                              const dataMax = Math.max(...chartData.map(d => d.apiCalls), 25);
                              const max = roundToNiceDomain(dataMax);
                              const ticks: number[] = [];
                              const step = max / 5;
                              for (let i = 0; i <= 5; i++) ticks.push(step * i);
                              return ticks;
                            })()}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            tickFormatter={(v: number) => v === 0 ? '' : formatNumber(v)}
                          />
                          <Tooltip
                            formatter={(v: number) => [formatNumber(v), 'Requests']}
                            labelFormatter={(_: any, payload: any) => payload?.[0]?.payload?.fullDate || _}
                            contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }}
                          />
                          <Area type="monotone" dataKey="apiCalls" stroke="#617BFF" fill="url(#colorRequests)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Repository Linked Card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div className="absolute w-full h-full rounded-xl" style={{ backgroundColor: '#FDFDFD', borderRadius: '12px', border: '1px solid #E9EAEB' }}>
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Repository Linked</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#717680' }}>{llmConfig?.repos?.length || 0} repositories</Typography>
                  </div>
                </div>
                <div className="absolute bg-white rounded-xl shadow-sm" style={{ width: '528px', height: '330px', top: '42px', borderRadius: '12px', border: '1px solid #E9EAEB', overflow: 'hidden' }}>
                  <div className="p-4 h-full flex flex-col">
                    <div className="flex items-center mb-4">
                      <Typography variant="h5" className="font-bold text-gray-900">
                        {llmConfig?.repos?.length || 0}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#717680', ml: 1 }}>linked</Typography>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {llmConfig?.repos?.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <div style={{ width: 48, height: 48, borderRadius: 10, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                            </svg>
                          </div>
                          <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>No repositories linked</Typography>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {llmConfig?.repos?.map((repo: { id: string; name: string }) => (
                            <div
                              key={repo.id}
                              onClick={() => navigate(`/repositories/inventory/${repo.id}`)}
                              className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                                  </svg>
                                </div>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>{repo.name}</Typography>
                              </div>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Token Usage Card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div className="absolute w-full h-full rounded-xl" style={{ backgroundColor: '#FDFDFD', borderRadius: '12px', border: '1px solid #E9EAEB' }}>
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Token Usage (30d)</Typography>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon sx={{ fontSize: 14, color: '#617BFF' }} />
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#535862' }}>{dailyStatsLoading ? '...' : formatTokens(totalTokensInFromChart)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon sx={{ fontSize: 14, color: '#10B981' }} />
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#535862' }}>{dailyStatsLoading ? '...' : formatTokens(totalTokensOutFromChart)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute bg-white rounded-xl shadow-sm" style={{ width: '528px', height: '330px', top: '42px', borderRadius: '12px', border: '1px solid #E9EAEB', overflow: 'visible' }}>
                  <div className="p-4">
                    <div style={{ height: 290 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <defs>
                            <linearGradient id="colorTokensIn" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#617BFF" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorTokensOut" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            interval={2}
                          />
                          <YAxis
                            domain={[0, (dataMax: number) => roundToNiceDomain(Math.max(dataMax, 25))]}
                            ticks={(() => {
                              const allValues = [...chartData.map(d => d.tokensIn), ...chartData.map(d => d.tokensOut)];
                              const dataMax = Math.max(...allValues, 25);
                              const max = roundToNiceDomain(dataMax);
                              const ticks: number[] = [];
                              const step = max / 5;
                              for (let i = 0; i <= 5; i++) ticks.push(step * i);
                              return ticks;
                            })()}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            tickFormatter={(v: number) => v === 0 ? '' : formatNumber(v)}
                          />
                          <Tooltip
                            formatter={(v: number, name: string) => [formatNumber(v), name === 'tokensIn' ? 'Tokens In' : 'Tokens Out']}
                            labelFormatter={(_: any, payload: any) => payload?.[0]?.payload?.fullDate || _}
                            contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }}
                          />
                          <Area type="monotone" dataKey="tokensIn" stroke="#617BFF" fill="url(#colorTokensIn)" strokeWidth={2} />
                          <Area type="monotone" dataKey="tokensOut" stroke="#10B981" fill="url(#colorTokensOut)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Policy Alerts Card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div className="absolute w-full h-full rounded-xl" style={{ backgroundColor: '#FDFDFD', borderRadius: '12px', border: '1px solid #E9EAEB' }}>
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Policy Alerts (30d)</Typography>
                    <Typography style={{ color: '#535862', fontWeight: 500, fontSize: '13px' }}>{dailyStatsLoading ? '...' : `${formatNumber(totalViolationsFromChart)} Alerts`}</Typography>
                  </div>
                </div>
                <div className="absolute bg-white rounded-xl shadow-sm" style={{ width: '528px', height: '330px', top: '42px', borderRadius: '12px', border: '1px solid #E9EAEB', overflow: 'visible' }}>
                  <div className="p-4">
                    <div style={{ height: 290 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <defs>
                            <linearGradient id="colorViolations" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            interval={2}
                          />
                          <YAxis
                            domain={[0, (dataMax: number) => roundToNiceDomain(Math.max(dataMax, 25))]}
                            ticks={(() => {
                              const dataMax = Math.max(...chartData.map(d => d.policyViolations), 25);
                              const max = roundToNiceDomain(dataMax);
                              const ticks: number[] = [];
                              const step = max / 5;
                              for (let i = 0; i <= 5; i++) ticks.push(step * i);
                              return ticks;
                            })()}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            tickFormatter={(v: number) => v === 0 ? '' : formatNumber(v)}
                          />
                          <Tooltip
                            formatter={(v: number) => [formatNumber(v), 'Violations']}
                            labelFormatter={(_: any, payload: any) => payload?.[0]?.payload?.fullDate || _}
                            contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }}
                          />
                          <Area type="monotone" dataKey="policyViolations" stroke="#F59E0B" fill="url(#colorViolations)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Bridge Nodes Table */}
          <div className="flex justify-center mb-6">
            <div className="relative" style={{ width: '1080px' }}>
              <div className="w-full bg-[#FDFDFD] rounded-xl border border-[#E9EAEB]" style={{ minHeight: '200px', position: 'absolute', zIndex: 1 }}></div>
              <div style={{ height: '42px', display: 'flex', alignItems: 'center', paddingLeft: '16px', position: 'relative', zIndex: 2 }}>
                <Typography style={{ color: '#181D27', fontWeight: 600, fontSize: '14px' }}>Bridge Nodes</Typography>
                <Typography sx={{ fontSize: '12px', color: '#717680', ml: 2 }}>({bridgeNodes.length})</Typography>
              </div>
              <div className="w-full bg-white border border-[#E9EAEB] shadow-sm" style={{ minHeight: '160px', borderRadius: '12px', padding: '16px', position: 'relative', zIndex: 2 }}>
                {loadingNodes ? (
                  <div className="flex items-center justify-center" style={{ minHeight: '120px' }}>
                    <CircularProgress size={24} style={{ color: '#617BFF' }} />
                  </div>
                ) : bridgeNodes.length === 0 ? (
                  <div className="flex items-center justify-center" style={{ minHeight: '120px' }}>
                    <Typography sx={{ color: '#6B7280' }}>No bridge nodes connected</Typography>
                  </div>
                ) : (
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Node ID</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Host</th>
                        <th className="text-center py-2 px-3 text-xs font-medium text-gray-500 uppercase">Latency</th>
                        <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bridgeNodes.map((node, index) => (
                        <tr key={node.nodeId || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <div style={{ width: 28, height: 28, borderRadius: 6, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                                  <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                                  <line x1="6" y1="6" x2="6.01" y2="6"/>
                                  <line x1="6" y1="18" x2="6.01" y2="18"/>
                                </svg>
                              </div>
                              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>
                                {node.nodeId.length > 20 ? node.nodeId.slice(0, 20) + '...' : node.nodeId}
                              </Typography>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <Typography sx={{ fontSize: '13px', color: '#717680' }}>{node.host || '-'}</Typography>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Typography sx={{ fontSize: '13px', color: '#717680' }}>{node.latency} ms</Typography>
                          </td>
                          <td className="py-3 px-3 text-right">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              node.status === 'ONLINE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {node.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete AI LLM Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the AI LLM configuration "{llmConfig?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!llmConfig) return;
              
              try {
                const resp = await withTokenRefresh(() => apiClient.deleteAiLlm(llmConfig.id));
                if (resp.success) {
                  toastSuccess('AI LLM configuration deleted successfully');
                  setDeleteDialogOpen(false);
                  navigate('/ai/llm');
                } else {
                  toastError((resp as any).message || 'Failed to delete AI LLM configuration');
                }
              } catch (error) {
                console.error('[AILLMDetails] Error deleting AI LLM:', error);
                toastError('Failed to delete AI LLM configuration');
              }
            }}
            style={{ color: '#DC2626' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AILLMDetails;
